import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import {
  downloadCsv,
  getOpsEvalsFiltered,
  lastSevenDays,
} from '../../../requests/opsReports';
import {
  btnSecondary,
  Card,
  inputClass,
  labelClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
} from '../ui';

const statCard = 'rounded-xl border border-white-regular bg-white px-5 py-4';

const OpsEvals: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountId = searchParams.get('accountId') ?? '';
  const urlLabel = searchParams.get('label') ?? '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState<'' | 'Public' | 'Private'>('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const filters = {
    q: q || undefined,
    from,
    to,
    type,
    accountId: urlAccountId || undefined,
  };
  const { data, isLoading } = useQuery({
    queryKey: ['opsEvals', q, from, to, type, urlAccountId, page],
    queryFn: () => getOpsEvalsFiltered({ ...filters, page }),
  });

  const setWindow = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    setPage(1);
  };

  const csv = async () => {
    setBusy(true);
    try {
      await downloadCsv(
        'ops/evals',
        filters,
        `evaluations${from ? `-${from}` : ''}${to ? `-to-${to}` : ''}.csv`,
      );
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Evaluations"
        meta="Rollups are per course and per account — deliberately never per instructor"
      />
      <Toolbar>
        {urlAccountId && (
          <button
            type="button"
            className="rounded-full border border-blue-200 bg-blue-100/60 px-2.5 py-1 text-xs text-blue-primary"
            onClick={() => setSearchParams({})}
            title="Every account"
          >
            {urlLabel || 'one account'} only ×
          </button>
        )}
        <input
          className={`${inputClass} w-72`}
          placeholder="Search by course or account…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-three">
          <span className={`${labelClass} mb-0`}>From</span>
          <input
            type="date"
            className={inputClass}
            value={from}
            onChange={(e) => setWindow(e.target.value, to)}
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-three">
          <span className={`${labelClass} mb-0`}>To</span>
          <input
            type="date"
            className={inputClass}
            value={to}
            onChange={(e) => setWindow(from, e.target.value)}
          />
        </label>
        <button
          type="button"
          className={btnSecondary}
          onClick={() => {
            const w = lastSevenDays();
            setWindow(w.from, w.to);
          }}
        >
          Last 7 days
        </button>
        {(from || to) && (
          <button
            type="button"
            className="text-xs font-medium text-gray-bold underline hover:text-blue-primary"
            onClick={() => setWindow('', '')}
          >
            clear dates
          </button>
        )}
        <select
          className={inputClass}
          value={type}
          onChange={(e) => {
            setType(e.target.value as '' | 'Public' | 'Private');
            setPage(1);
          }}
        >
          <option value="">Public and private</option>
          <option value="Public">Public</option>
          <option value="Private">Private</option>
        </select>
        <span className="flex-1" />
        <button
          type="button"
          className={btnSecondary}
          onClick={csv}
          disabled={busy}
        >
          {busy ? 'Preparing…' : 'Download CSV'}
        </button>
      </Toolbar>

      <PageBody wide>
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={statCard}>
            <div className="text-3xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
              {data?.total?.toLocaleString() ?? '…'}
            </div>
            <div className="mt-0.5 text-sm text-gray-three">responses</div>
          </div>
          <div className={statCard}>
            <div className="text-3xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
              {data?.avgRating ?? '…'}
            </div>
            <div className="mt-0.5 text-sm text-gray-three">
              avg course rating (of 5)
            </div>
          </div>
          <div className={statCard}>
            <div className="text-3xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
              {data?.avgRecommend ?? '…'}
            </div>
            <div className="mt-0.5 text-sm text-gray-three">
              avg recommend (of 10)
            </div>
          </div>
          <div className={`${statCard} flex items-end gap-2`}>
            {data?.byYear.slice(-6).map((y) => (
              <div key={y.year} className="text-center">
                <div className="text-[10px] text-gray-three [font-variant-numeric:tabular-nums]">
                  {y.n}
                </div>
                <div
                  className="mx-auto w-6 rounded-t bg-blue-primary/70"
                  style={{ height: `${Math.max(4, (y.n / 11000) * 48)}px` }}
                />
                <div className="text-[10px] text-gray-two">{y.year}</div>
              </div>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[900px]`}>
                <thead className={theadClass}>
                  <tr>
                    <th className={thClass}>Date</th>
                    <th className={thClass}>Course</th>
                    <th className={thClass}>Account</th>
                    <th className={thClass}>Type</th>
                    <th className={`${thClass} text-right`}>Rating</th>
                    <th className={`${thClass} text-right`}>Rec.</th>
                    <th className={thClass}>Top takeaway</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((e) => (
                    <tr key={e.id}>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {e.date?.slice(0, 10) ?? '—'}
                      </td>
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {e.course ?? '—'}
                        {e.localOrigin && (
                          <span className="ml-2">
                            <Pill value="via webhook" tone="info" />
                          </span>
                        )}
                      </td>
                      <td className={tdClass}>{e.account ?? '—'}</td>
                      <td className={tdClass}>
                        {(e as { type?: string | null }).type ?? '—'}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {e.rating ?? '—'}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {e.recommend ?? '—'}
                      </td>
                      <td
                        className={`${tdClass} max-w-md truncate text-gray-three`}
                      >
                        {e.takeaway ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        )}
      </PageBody>
    </>
  );
};

export default OpsEvals;
